<template>
  <el-dialog
    :visible="visible"
    width="40%"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <ContentHeader
      slot="title"
      :title="$t('stability.timePoints.fileTitle')"
    />

    <el-descriptions
      :column="1"
      border
      label-class-name="time-point-file-label"
    >
      <el-descriptions-item :label="$t('stability.token')">
        {{ timePoint ? timePoint.token : '-' }}
      </el-descriptions-item>
    </el-descriptions>

    <!-- A list rather than an el-table: every file is a row with its own
         download and remove, and nothing here needs sorting or paging -->
    <ul
      v-if="files.length"
      class="time-point-files"
    >
      <li
        v-for="timePointFile in files"
        :key="timePointFile.id"
        class="time-point-file"
      >
        <el-button
          class="time-point-file-name"
          type="text"
          icon="fas fa-download"
          :loading="downloadingId === timePointFile.id"
          @click="download(timePointFile)"
        >
          {{ timePointFile.file.filename }}
        </el-button>
        <span class="time-point-file-date">
          {{ $t('stability.timePoints.fileUpdatedAt') }}
          {{ formatUploadedAt(timePointFile) }}
        </span>
        <!-- Removal needs stabilityConfigManage; read-only users only download -->
        <el-button
          v-if="canManage"
          class="time-point-file-remove"
          type="text"
          icon="fas fa-trash-alt"
          :loading="removingId === timePointFile.id"
          :disabled="removingId !== null && removingId !== timePointFile.id"
          @click="confirmRemove(timePointFile)"
        >
          {{ $t('stability.timePoints.fileRemove') }}
        </el-button>
      </li>
    </ul>
    <p
      v-else
      class="time-point-files-empty"
    >
      {{ $t('stability.timePoints.fileNone') }}
    </p>

    <!-- Upload needs stabilityConfigManage; read-only users only download -->
    <template v-if="canManage">
      <el-divider />

      <el-upload
        ref="upload"
        action=""
        :http-request="upload"
        :before-upload="assertAllowedExtension"
        :show-file-list="false"
        :multiple="false"
        :limit="1"
        :accept="acceptedExtensions"
        :disabled="uploading"
      >
        <el-button
          type="primary"
          icon="el-icon-upload2"
          :loading="uploading"
        >
          {{ $t('stability.timePoints.fileUpload') }}
        </el-button>
      </el-upload>

      <el-alert
        class="time-point-file-hint"
        :title="$t('stability.timePoints.fileHint')"
        type="info"
        :closable="false"
      />
    </template>

    <span slot="footer">
      <el-button
        :disabled="uploading"
        @click="handleClose"
      >
        {{ $t('global.close') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import fileSaver from 'file-saver';
  import { stabilityApi, filesApi } from '@/api';
  import ContentHeader from '@/components/ContentHeader.vue';

  // The API validates by extension, not by media type, because browsers
  // routinely declare xlsx as `application/octet-stream`. Mirrored here so the
  // rejection happens before the upload rather than as a 422.
  const ALLOWED_EXTENSIONS = ['.pdf', '.xlsx'];

  export default {
    name: 'TimePointFileDialog',
    components: { ContentHeader },

    props: {
      visible: { type: Boolean, required: true },
      configId: { type: Number, required: true },
      timePoint: { type: Object, default: null },
      canManage: { type: Boolean, default: false },
    },

    data() {
      return {
        uploading: false,
        // Per row: the files are independent, a download or a removal in
        // progress on one must not show on the others
        downloadingId: null,
        removingId: null,
      };
    },

    computed: {
      acceptedExtensions() {
        return ALLOWED_EXTENSIONS.join(',');
      },
      // The API lists only the files not removed, in upload order
      files() {
        return (this.timePoint && this.timePoint.files) || [];
      },
    },

    methods: {
      formatUploadedAt(timePointFile) {
        const { updatedAt } = timePointFile.file;
        return updatedAt ? moment(updatedAt).format('DD/MM/YYYY HH:mm') : '-';
      },

      handleClose() {
        this.$emit('update:visible', false);
        if (this.$refs.upload) this.$refs.upload.clearFiles();
      },

      // Returning false aborts the upload without reaching http-request
      assertAllowedExtension(file) {
        const name = (file.name || '').toLowerCase();
        if (ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext))) return true;
        this.$notify({
          type: 'error',
          showClose: true,
          message: this.$t('stability.timePoints.fileWrongType', {
            extensions: ALLOWED_EXTENSIONS.join(', '),
          }),
        });
        return false;
      },

      // el-upload hands over the file and expects us to do the request: going
      // through the api client keeps the Authorization header the axios
      // interceptor adds, which el-upload's own XHR would not carry.
      async upload({ file }) {
        // `timePoint` is derived from the parent's list: if the row went away
        // there is nothing to attach the file to
        if (!this.timePoint) return;
        this.uploading = true;
        try {
          await stabilityApi.uploadTimePointFile(
            this.configId,
            this.timePoint.id,
            file
          );
          this.$emit('changed');
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.uploading = false;
          if (this.$refs.upload) this.$refs.upload.clearFiles();
        }
      },

      async download(timePointFile) {
        const { file } = timePointFile;
        this.downloadingId = timePointFile.id;
        try {
          const { data } = await filesApi.download(file.id);
          fileSaver.saveAs(data, file.filename);
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.downloadingId = null;
        }
      },

      // Takes the id of the time point file, not of the file row: the route
      // addresses the attachment, `file.id` only serves the download
      async confirmRemove(timePointFile) {
        if (!this.timePoint) return;
        try {
          await this.$confirm(
            this.$t('stability.timePoints.fileRemoveConfirm', {
              name: timePointFile.file.filename,
            }),
            this.$t('global.warning'),
            {
              confirmButtonText: this.$t('stability.timePoints.fileRemove'),
              cancelButtonText: this.$t('global.cancel'),
            }
          );
        } catch {
          return;
        }

        this.removingId = timePointFile.id;
        try {
          await stabilityApi.removeTimePointFile(
            this.configId,
            this.timePoint.id,
            timePointFile.id
          );
          this.$emit('changed');
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.removingId = null;
        }
      },

      notifyError(err) {
        this.$notify({
          type: 'error',
          showClose: true,
          message:
            (err.response &&
              err.response.data &&
              err.response.data.error &&
              err.response.data.error.message) ||
            this.$t('stability.errors.generic'),
        });
      },
    },
  };
</script>

<style lang="scss">
  // Not scoped: every element styled here is created by a child component (the
  // descriptions cell, the alert root, the button), not by this template, so
  // whether the scope id reaches them depends on Vue's propagation into slot
  // content. The class names are unique enough to stand on their own.

  // el-button is white-space: nowrap, so a long file name would overflow the
  // descriptions cell instead of wrapping. break-all because file names carry
  // no spaces to break on.
  // Compounded with the element-ui class so the rule wins on specificity
  // rather than on which chunk happens to load last.
  .el-button.time-point-file-name {
    white-space: normal;
    word-break: break-all;
    text-align: left;
  }

  // .el-descriptions__table is table-layout: fixed, where min-width on a cell
  // is ignored and only width sets the column: without this a long file name
  // squeezes the label column down to a couple of characters.
  .el-descriptions-item__label.time-point-file-label {
    width: 30%;
  }

  .time-point-files {
    list-style: none;
    margin: 0.75rem 0 0;
    padding: 0;
  }

  .time-point-file {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  // Pushes the remove action to the right edge of the row
  .time-point-file-date {
    margin-left: auto;
    white-space: nowrap;
  }

  .time-point-files-empty {
    margin: 0.75rem 0 0;
  }

  // el-alert sets margin: 0
  .el-alert.time-point-file-hint {
    margin-top: 0.75rem;
  }
</style>
